/**
 * Tenant Service
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import type {
  PlatformStats,
  TenantWithSubscription,
  TenantDetail,
  TenantStatus,
} from '../types';

export interface TenantOverview {
  tenants: TenantWithSubscription[];
  total: number;
}

export interface CreateTenantDto {
  name: string;
  slug: string;
  environment?: string; // 'production' | 'staging' | 'test'
  adminUser?: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  createTestEnvironment?: boolean;
}

export const tenantService = {
  /**
   * Get platform stats
   */
  async getStats(): Promise<PlatformStats> {
    const response = await apiClient.get<PlatformStats>(API_ENDPOINTS.TENANT_STATS);
    return response.data;
  },

  /**
   * Get all tenants with subscription info
   */
  async getAll(): Promise<TenantOverview> {
    const response = await apiClient.get<TenantOverview>(API_ENDPOINTS.TENANTS);
    return response.data;
  },

  /**
   * Get a single tenant with full details
   */
  async getById(id: string): Promise<TenantDetail> {
    const response = await apiClient.get<TenantDetail>(`${API_ENDPOINTS.TENANTS}/${id}`);
    return response.data;
  },

  /**
   * Create a new tenant
   */
  async create(dto: CreateTenantDto): Promise<TenantDetail> {
    const response = await apiClient.post<TenantDetail>(API_ENDPOINTS.TENANTS, dto);
    return response.data;
  },

  /**
   * Update tenant status
   */
  async updateStatus(id: string, status: TenantStatus): Promise<TenantDetail> {
    const response = await apiClient.patch<TenantDetail>(
      `${API_ENDPOINTS.TENANTS}/${id}/status`,
      { status }
    );
    return response.data;
  },

  /**
   * Delete a tenant
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.TENANTS}/${id}`);
  },

  /**
   * Create an environment for a tenant
   */
  async createEnvironment(
    tenantId: string,
    name: string,
    adminUser?: CreateTenantDto['adminUser']
  ): Promise<void> {
    await apiClient.post(`${API_ENDPOINTS.TENANTS}/${tenantId}/environments`, {
      name,
      adminUser,
    });
  },
};
